"use client";

import { useState, useMemo, useEffect } from "react";
import type { Transparencia } from "@/lib/api/datosGenerales/types/transparencia";
import HTMLReactParser from "html-react-parser";
import ExternalLinkModal from "@/components/ExternalLinkModal/ExternalLinkModal";
import { useTranslations } from "next-intl";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ExternalLink, Search, X, Send, Loader2 } from "lucide-react";

import { GrupoAccordion, DocumentosSinGrupo, FilterDropdown } from "./components";

export default function TransparenciaComponent(data: Transparencia) {
  const t = useTranslations("transparenciaPage");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedYear, setSelectedYear] = useState<string>("all");
  const [selectedOption, setSelectedOption] = useState<null | {
    label: string;
    href: string;
  }>(null);
  const [openArticulos, setOpenArticulos] = useState<string>("");
  const [openSubarticulos, setOpenSubarticulos] = useState<string[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (!searchQuery) {
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    const timer = setTimeout(() => setIsSearching(false), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleContinue = () => {
    if (selectedOption) {
      window.open(selectedOption.href, "_blank");
      setSelectedOption(null);
    }
  };

  const availableYears = useMemo(() => {
    const years = new Set<number>();
    data.articulos.forEach((articulo) => {
      articulo.subarticulos.forEach((subarticulo) => {
        subarticulo.grupos.forEach((grupo) => {
          grupo.documentosPorAnio.forEach((docAnio) => years.add(docAnio.anio));
        });
        subarticulo.documentosSinGrupoPorAnio.forEach((docAnio) =>
          years.add(docAnio.anio),
        );
      });
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [data]);

  const filteredData = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const yearFilter = selectedYear !== "all" ? parseInt(selectedYear) : null;

    const filterDocumentos = (
      documentos: {
        id: number;
        titulo: string;
        archivo: string;
        formato: string;
        anio: number;
      }[],
    ) =>
      documentos.filter(
        (doc) => query === "" || doc.titulo.toLowerCase().includes(query),
      );

    const filterDocumentosPorAnio = (
      documentosPorAnio: {
        anio: number;
        documentos: {
          id: number;
          titulo: string;
          archivo: string;
          formato: string;
          anio: number;
        }[];
      }[],
    ) =>
      documentosPorAnio
        .filter((docAnio) => yearFilter === null || docAnio.anio === yearFilter)
        .map((docAnio) => ({
          ...docAnio,
          documentos: filterDocumentos(docAnio.documentos),
        }))
        .filter((docAnio) => docAnio.documentos.length > 0);

    const filteredArticulos = data.articulos
      .map((articulo) => ({
        ...articulo,
        subarticulos: articulo.subarticulos
          .map((subarticulo) => ({
            ...subarticulo,
            grupos: subarticulo.grupos
              .map((grupo) => ({
                ...grupo,
                documentosPorAnio: filterDocumentosPorAnio(
                  grupo.documentosPorAnio,
                ),
              }))
              .filter((grupo) => grupo.documentosPorAnio.length > 0),
            documentosSinGrupoPorAnio: filterDocumentosPorAnio(
              subarticulo.documentosSinGrupoPorAnio,
            ),
          }))
          .filter(
            (subarticulo) =>
              subarticulo.grupos.length > 0 ||
              subarticulo.documentosSinGrupoPorAnio.length > 0,
          ),
      }))
      .filter((articulo) => articulo.subarticulos.length > 0);

    return { ...data, articulos: filteredArticulos };
  }, [data, searchQuery, selectedYear]);

  const totalDocuments = useMemo(() => {
    let count = 0;
    filteredData.articulos.forEach((articulo) => {
      articulo.subarticulos.forEach((subarticulo) => {
        subarticulo.grupos.forEach((grupo) => {
          grupo.documentosPorAnio.forEach((docAnio) => {
            count += docAnio.documentos.length;
          });
        });
        subarticulo.documentosSinGrupoPorAnio.forEach((docAnio) => {
          count += docAnio.documentos.length;
        });
      });
    });
    return count;
  }, [filteredData]);

  useEffect(() => {
    if (searchQuery.trim() !== "" || selectedYear !== "all") {
      if (filteredData.articulos.length > 0) {
        setOpenArticulos(`articulo-${filteredData.articulos[0].id}`);
        const subarticulos = filteredData.articulos[0].subarticulos.map(
          (sub) => `subarticulo-${sub.id}`,
        );
        setOpenSubarticulos(subarticulos);
      }
    } else {
      setOpenArticulos("");
      setOpenSubarticulos([]);
    }
  }, [searchQuery, selectedYear, filteredData]);

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedYear("all");
  };

  const hasActiveFilters = searchQuery !== "" || selectedYear !== "all";

  const resultsLabel =
    totalDocuments === 1
      ? t("resultsOne", { count: totalDocuments })
      : t("resultsMany", { count: totalDocuments });

  const yearOptions = availableYears.map((y) => ({
    value: y.toString(),
    label: y.toString(),
  }));

  return (
    <div>
      {/* Descripción */}
      <div className="max-w-3xl mb-12">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
          {data.titulo}
        </h2>
        <div className="prose prose-gray max-w-none text-gray-600 leading-relaxed">
          {HTMLReactParser(data.descripcion)}
        </div>
      </div>

      {/* Enlaces externos */}
      <div className="flex flex-wrap gap-3 mb-14 pb-12 border-b border-gray-200">
        <button
          onClick={() =>
            setSelectedOption({
              label: t("linkDatosAbiertos"),
              href: "https://www.datosabiertos.gob.pa/organization/tocumen-aeropuerto-internacional-de-panama",
            })
          }
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#102542] text-white rounded-full text-xs uppercase tracking-wider font-semibold hover:bg-[#0a1a30] transition-colors"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          {t("linkDatosAbiertos")}
        </button>
        <button
          onClick={() =>
            setSelectedOption({
              label: t("linkAntai"),
              href: "https://monitoreo.antai.gob.pa/",
            })
          }
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-[#102542] border border-[#102542] rounded-full text-xs uppercase tracking-wider font-semibold hover:bg-gray-50 transition-colors"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          {t("linkAntai")}
        </button>
        <button
          onClick={() =>
            setSelectedOption({
              label: t("linkSolicitar"),
              href: "https://monitoreo.antai.gob.pa/registrarse",
            })
          }
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-gray-700 border border-gray-200 rounded-full text-xs uppercase tracking-wider font-semibold hover:border-[#102542] hover:text-[#102542] transition-colors"
        >
          <Send className="w-3.5 h-3.5" />
          {t("linkSolicitar")}
        </button>
      </div>

      <ExternalLinkModal
        isOpen={!!selectedOption}
        onClose={() => setSelectedOption(null)}
        onContinue={handleContinue}
      />

      {/* Filtros */}
      <div className="space-y-4 mb-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="relative w-full md:max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t("searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-10 py-2.5 bg-white border border-gray-200 rounded-full text-sm
                 focus:outline-none focus:border-[#102542] transition-colors text-gray-900 placeholder-gray-400"
            />
            {isSearching && searchQuery && (
              <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#102542] animate-spin" />
            )}
            {searchQuery && !isSearching && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 transition-colors"
                aria-label={t("clearFilters")}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {yearOptions.length > 0 && (
            <FilterDropdown
              label={t("filterYear")}
              value={selectedYear}
              options={yearOptions}
              onChange={setSelectedYear}
              allLabel={t("filterAll")}
            />
          )}
        </div>

        <div className="flex items-center justify-between text-xs uppercase tracking-wider">
          <span className="text-gray-500 font-mono">{resultsLabel}</span>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 text-[#102542] font-semibold hover:gap-2 transition-all"
            >
              <X className="w-3.5 h-3.5" />
              {t("clearFilters")}
            </button>
          )}
        </div>
      </div>

      {/* No results */}
      {filteredData.articulos.length === 0 && (
        <div className="text-center py-16">
          <h3 className="text-xl font-bold text-gray-900 mb-2 tracking-tight">
            {t("noResults")}
          </h3>
          <p className="text-gray-600 mb-6">{t("noResultsDescription")}</p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#102542] text-white rounded-full text-sm font-semibold hover:bg-[#0a1a30] transition-colors"
            >
              <X className="w-4 h-4" />
              {t("clearFilters")}
            </button>
          )}
        </div>
      )}

      {/* Artículos */}
      {filteredData.articulos.length > 0 && (
        <Accordion
          type="single"
          collapsible
          className="space-y-3"
          value={openArticulos}
          onValueChange={setOpenArticulos}
        >
          {filteredData.articulos.map((articulo) => (
            <AccordionItem
              key={articulo.id}
              value={`articulo-${articulo.id}`}
              className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm"
            >
              <AccordionTrigger className="px-6 py-5 hover:no-underline hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-4 text-left flex-1">
                  <span className="shrink-0 inline-flex items-center justify-center min-w-[3rem] h-10 px-3 rounded-full bg-[#102542] text-white text-xs font-bold uppercase tracking-wider tabular-nums">
                    {articulo.codigo}
                  </span>
                  <div className="flex-1">
                    <h3 className="font-semibold text-base md:text-lg text-gray-900 tracking-tight">
                      {articulo.nombre}
                    </h3>
                    {articulo.label && (
                      <p className="text-xs text-gray-500 mt-1">
                        {articulo.label}
                      </p>
                    )}
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6 pt-2 border-t border-gray-100 bg-gray-50/60">
                <Accordion
                  type="multiple"
                  className="space-y-2 mt-4"
                  value={openSubarticulos}
                  onValueChange={setOpenSubarticulos}
                >
                  {articulo.subarticulos.map((subarticulo) => (
                    <AccordionItem
                      key={subarticulo.id}
                      value={`subarticulo-${subarticulo.id}`}
                      className="bg-white border border-gray-200 rounded-xl overflow-hidden"
                    >
                      <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-3 text-left flex-1 min-w-0">
                          <span className="shrink-0 text-xs font-mono font-semibold text-[#102542] border border-[#102542]/30 bg-[#102542]/5 px-2 py-0.5 rounded">
                            {subarticulo.codigo}
                          </span>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-sm text-gray-900 leading-snug">
                              {subarticulo.nombre}
                            </h4>
                            {subarticulo.historico && (
                              <span className="inline-block text-[10px] uppercase tracking-wider text-amber-700 border border-amber-200 bg-amber-50 px-2 py-0.5 rounded mt-1.5">
                                {t("historicoLabel")}
                              </span>
                            )}
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4 pt-2 border-t border-gray-100 bg-gray-50/60">
                        <div className="space-y-4 overflow-hidden pt-2">
                          <GrupoAccordion grupos={subarticulo.grupos} />
                          <DocumentosSinGrupo
                            documentosPorAnio={
                              subarticulo.documentosSinGrupoPorAnio
                            }
                          />
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
}
